import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import routes from "./Routes";
import PublicRoute from "./components/auth/PublicRoute";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PageMeta from "./components/common/PageMeta";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";

// Lazy-load public auth pages (kept in App as requested)
const Login = lazy(() => import("./pages/Auth/LoginForm"));
const SignUp = lazy(() => import("./pages/Auth/SignupForm"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const MarketingPageView = lazy(() => import("./pages/marketing/MarketingPageView"));

// Lightweight fallback used during lazy loading
const LoaderFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-600" />
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Optionally clear storage or report error
    // clearLocalStorage();
    console.error("Error boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const reportUrl = "/support?from_error=1&page=" + encodeURIComponent(typeof window !== "undefined" ? window.location.pathname : "");
      return (
        <div className="min-h-screen flex items-center justify-center bg-app p-6">
          <div className="max-w-md w-full card-surface rounded-lg shadow p-6 text-center">
            <h4 className="text-lg font-semibold text-theme mb-2">
              Something went wrong
            </h4>
            <p className="text-sm text-theme opacity-70 mb-4">
              There was an error loading this page. Try refreshing the page.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                className="px-3 py-1.5 btn-theme rounded text-xs hover:opacity-95 transition-opacity"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              <a
                href={reportUrl}
                className="px-3 py-1.5 border border-theme rounded text-xs hover:opacity-95 transition-opacity inline-block"
              >
                Report this issue
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="App">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        closeButton
      />
      <BrowserRouter>
        <PageMeta />
        <ErrorBoundary>
          <Suspense fallback={<LoaderFallback />}>
            <Routes>
              <Route path="/" element={<PublicRoute element={LandingPage} />} />
              <Route path="/login" element={<PublicRoute element={Login} />} />
              <Route
                path="/signup"
                element={<PublicRoute element={SignUp} />}
              />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/p/:slug" element={<MarketingPageView />} />

              {routes.map((routeConfig, index) => (
                <Route
                  key={index}
                  path={routeConfig.route}
                  element={
                    <ProtectedRoute
                      element={routeConfig.element}
                      withLayout={routeConfig.layout}
                    />
                  }
                />
              ))}

              <Route path="/notfound" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
